import React from 'react';
import { BrowserRouter, Switch, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { isAuthenticated } from './services/auth';
import 'react-toastify/dist/ReactToastify.css';
import './styles/global.css';
import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import '../node_modules/bootstrap/dist/js/bootstrap';
import 'semantic-ui-css/semantic.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Components
import { NavMenu, AddButton, Footer, Default, SearchResult, ScrollToTop, Chat, ChatList, UserNotifications } from './components';
import HomeContent from './pages/home';
import ErrorBoundary from './components/error-boundary';

// Pages
import {
  Login,
  Register,
  UserDashboard,
  AddCategory,
  AddLocation,
  EditLocation,
  EditCategory,
  ManageUsers,
  ReportedProducts,
  PrivateRoute,
  AdminRoute,
  UpsertProfile,
  UpdatePassword,
  ViewUser,
  RateUser,
  UpsertProduct,
  CategoryView,
  Favourites,
  FollowList,
  ViewProduct,
  SponsoredAdsAdmin,
  AdminUserProducts,
} from './pages';
import Home from './pages/home/home/index.js';

// Utils
import socket from './utils/socket';

function App() {
  const { user } = isAuthenticated();

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2', // Customize as needed
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  // Conecte o socket apenas uma vez, quando o usuário estiver autenticado
  React.useEffect(() => {
    if (user) {
      if (!socket.connected) {
        socket.connect();
        socket.emit('join', { userId: user._id });
      }
    }
    // Desconecta ao sair
    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [user]);

  React.useEffect(() => {
    document.title = process.env.REACT_APP_COMPANY_NAME || 'Default Company Name';
  }, []);

  const AppContent = () => {
    const location = useLocation();
    return (
      <div className='AppContent-wrapper'>
        <ScrollToTop />
        <ErrorBoundary name="NavMenu">
          <NavMenu />
        </ErrorBoundary>
        {/*<AddButton />*/}
        <Toaster toastOptions={{ style: { marginTop: 100 } }} />
        <ToastContainer position='top-center' style={{ marginTop: 100 }} />
        <Switch>
        <Route exact path='/' component={Home} />
        <Route exact path='/login' component={user ? UserDashboard : Login} />
        <Route
          exact
          path='/register'
          component={user ? UserDashboard : Register}
        />
        <Route exact path='/search-result' component={SearchResult} />
        <Route exact path='/category/:categoryId' component={CategoryView} />
        <Route exact path='/product/:productId' component={ViewProduct} />
        <PrivateRoute exact path='/user/dashboard' component={UserDashboard} />
        <PrivateRoute
          exact
          path='/user/notifications'
          component={UserNotifications}
        />
        <PrivateRoute exact path='/messages' component={ChatList} />
        <PrivateRoute exact path='/user/message/:chatId' component={Chat} />
        {/*<PrivateRoute exact path='/user/business' component={BusinessInfo} />*/}
        <PrivateRoute exact path='/user/follow-list' component={FollowList} />
        <PrivateRoute exact path='/user/favourites' component={Favourites} />
        <Route exact path='/user/:userId' component={ViewUser} />
        <PrivateRoute exact path='/rate/user/:userId' component={RateUser} />
        <PrivateRoute 
          exact 
          path='/user/edit/:userId' 
          component={(props) => <UpsertProfile {...props} hideBackButton={true} />} 
        />
        <PrivateRoute
          exact
          path='/user/password/:userId'
          component={UpdatePassword}
        />
        <PrivateRoute
          exact
          path='/add-product/:userId'
          component={(props) => <UpsertProduct {...props} hideBackButton={true} />}
        />
        <PrivateRoute
          exact
          path='/edit-product/:productId'
          component={UpsertProduct}
        />
        <AdminRoute exact path='/user/dashboard' component={UserDashboard} />
        <AdminRoute exact path='/admin/add-category' component={AddCategory} />
        <AdminRoute exact path='/admin/add-location' component={AddLocation} />
        <AdminRoute
          exact
          path='/admin/category/edit/:categoryId'
          component={EditCategory}
        />
        <AdminRoute
          exact
          path='/admin/location/edit/:locationId'
          component={EditLocation}
        />
        <AdminRoute exact path='/admin/users' component={ManageUsers} />
        <AdminRoute 
          exact 
          path='/admin/user/edit/:userId' 
          component={(props) => <UpsertProfile {...props} hideBackButton={false} />} 
        />
        <AdminRoute
          exact
          path='/admin/reported-products'
          component={ReportedProducts}
        />
        <AdminRoute
          exact
          path='/admin/add-product/:userId'
          component={(props) => <UpsertProduct {...props} returnUrl='/admin/users' hideBackButton={false} />}
        />
        <AdminRoute
          exact
          path='/admin/sponsored-ads'
          component={SponsoredAdsAdmin}
        />
        <AdminRoute exact path='/admin/user-products/:userId' component={AdminUserProducts} />
        <Route component={Default} />
      </Switch>
       {location.pathname === "/" && <Footer />}
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
